import { useCallback, useEffect, useState } from 'react'
import { Stack, Group, Button, Table, Badge, ActionIcon, Text, Alert, Loader, Center, Tooltip, Paper } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconTrash, IconAlertCircle, IconKey } from '@tabler/icons-react'
import type { AdminPasskeyCredentialSummary } from '@paperair/shared-types'
import { listUserPasskeys, deleteUserPasskey, deleteAllUserPasskeys, getErrorMessage } from '../../api/usersApi'

function formatDateTime(value: Date | string | undefined): string {
  if (!value) return 'Never'
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

interface UserPasskeysSectionProps {
  userId: string
  userLabel: string
  onCountChange: (count: number) => void
}

export function UserPasskeysSection({ userId, userLabel, onCountChange }: UserPasskeysSectionProps) {
  const [passkeys, setPasskeys] = useState<AdminPasskeyCredentialSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [removingAll, setRemovingAll] = useState(false)

  const load = useCallback(() => {
    setError(null)
    listUserPasskeys(userId)
      .then((list) => {
        setPasskeys(list)
        onCountChange(list.length)
      })
      .catch((err) => setError(getErrorMessage(err)))
    // onCountChange is deliberately left out of the deps: it's a plain setState wrapper from the
    // parent, not stable-identity guaranteed, so including it would reload on every parent render.
  }, [userId])

  useEffect(() => {
    load()
  }, [load])

  const handleRemoveOne = useCallback(
    (passkey: AdminPasskeyCredentialSummary) => {
      modals.openConfirmModal({
        title: 'Remove passkey',
        children: (
          <Text size="sm">
            Remove <strong>{passkey.name}</strong> from {userLabel}? They will no longer be able to
            sign in with this passkey. They will be notified by email.
          </Text>
        ),
        labels: { confirm: 'Remove', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          setRemovingId(passkey.id)
          try {
            await deleteUserPasskey(userId, passkey.id)
            setPasskeys((prev) => {
              const next = (prev ?? []).filter((p) => p.id !== passkey.id)
              onCountChange(next.length)
              return next
            })
            notifications.show({ title: 'Passkey removed', message: `"${passkey.name}" has been removed`, color: 'green' })
          } catch (err) {
            notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
          } finally {
            setRemovingId(null)
          }
        },
      })
    },
    [userId, userLabel, onCountChange]
  )

  const handleRemoveAll = useCallback(() => {
    if (!passkeys || passkeys.length === 0) return
    modals.openConfirmModal({
      title: 'Remove all passkeys',
      children: (
        <Text size="sm">
          Remove all {passkeys.length} passkey{passkeys.length !== 1 ? 's' : ''} from {userLabel}? This
          can&apos;t be undone. They will fall back to their password and be notified by email.
        </Text>
      ),
      labels: { confirm: 'Remove all', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setRemovingAll(true)
        try {
          const { count } = await deleteAllUserPasskeys(userId)
          setPasskeys([])
          onCountChange(0)
          notifications.show({ title: 'Passkeys removed', message: `Removed ${count} passkey${count !== 1 ? 's' : ''}`, color: 'green' })
        } catch (err) {
          notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
        } finally {
          setRemovingAll(false)
        }
      },
    })
  }, [userId, userLabel, passkeys, onCountChange])

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        {error}
      </Alert>
    )
  }

  if (passkeys === null) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    )
  }

  return (
    <Stack gap="xs">
      {passkeys.length === 0 ? (
        <Text c="dimmed" size="sm">
          No passkeys enrolled
        </Text>
      ) : (
        <>
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconTrash size={14} />}
              loading={removingAll}
              onClick={handleRemoveAll}
            >
              Remove all
            </Button>
          </Group>
          <Paper withBorder style={{ overflowX: 'auto' }}>
            <Table verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Registered from</Table.Th>
                  <Table.Th>Last used</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {passkeys.map((passkey) => (
                  <Table.Tr key={passkey.id}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <IconKey size={14} />
                        <Text size="sm">{passkey.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={passkey.backedUp ? 'green' : 'gray'} variant="light" size="sm">
                        {passkey.backedUp ? 'Synced' : 'Device-only'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={passkey.registeredFromUserAgent || 'Unknown'} openDelay={300} multiline w={280}>
                        <div>
                          <Text size="sm">{passkey.registeredFromAppId}</Text>
                          <Text size="xs" c="dimmed" ff="monospace">{passkey.registeredFromIp || '—'}</Text>
                        </div>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {formatDateTime(passkey.lastUsedAt)}
                        {passkey.lastUsedFromAppId ? ` · ${passkey.lastUsedFromAppId}` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        loading={removingId === passkey.id}
                        onClick={() => handleRemoveOne(passkey)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  )
}
